/**
 * Axios Interceptor Configuration
 * 
 * Centralized token management and request/response interceptors.
 * Handles automatic token injection and 401 error handling.
 * Supports logout on authentication failure.
 */

import axios from 'axios';
import { ENDPOINT } from '../api/EndPoints';
import { logoutSync } from '../utils/auth/logoutUtility';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: ENDPOINT,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
let currentToken = null;
let tokenRefreshPromise = null;

/**
 * Get token from Redux store (localStorage)
 */
const getTokenFromStore = async () => {
  try {
    const userPersist = localStorage.getItem('persist:user');
    if (userPersist) {
      const parsedUser = JSON.parse(userPersist);
      if (parsedUser.USER_PARAM) {
        const userParam = JSON.parse(parsedUser.USER_PARAM);
        return userParam?.TOKEN || null;
      }
    }
  } catch (error) {
    console.error('Error retrieving token from store:', error);
  }
  return null;
};

/**
 * Update the current token
 */
export const setAuthToken = (token) => {
  currentToken = token;
  // Also store in window for cross-module access
  if (typeof window !== 'undefined') {
    window.__AXIOS_TOKEN__ = token;
  }
};

/**
 * Clear the current token
 */
export const clearAuthToken = () => {
  currentToken = null;
  if (typeof window !== 'undefined') {
    window.__AXIOS_TOKEN__ = null;
  }
};

/**
 * Get current token (with refresh if needed)
 */
export const getAuthToken = async () => {
  // If we have a cached token, return it
  if (currentToken) {
    return currentToken;
  }
  
  // If a token refresh is already in progress, wait for it
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }
  
  // Fetch token from store
  tokenRefreshPromise = getTokenFromStore();
  try {
    const token = await tokenRefreshPromise;
    currentToken = token;
    return token;
  } finally {
    tokenRefreshPromise = null;
  }
};

/**
 * Request Interceptor
 * Automatically adds Authorization header to all requests
 */
axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip token for login endpoint
    if (config.url && !config.url.includes('/login') && !config.url.includes('/Login')) {
      try {
        const token = await getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Error getting token for request:', error);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles 401 errors and automatically logs out
 */
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized
    if (error.response && error.response.status === 401) {
      // Prevent infinite loop
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        
        // Try to refresh token once
        try {
          const newToken = await getAuthToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            // Retry the original request
            return axiosInstance(originalRequest);
          }
        } catch (refreshError) {
          console.warn('Token refresh failed:', refreshError);
        }
        
        // If token refresh failed or this is not a login endpoint, logout
        if (!originalRequest.url.includes('/login') && !originalRequest.url.includes('/Login')) {
          console.warn('401 Unauthorized - logging out user');
          // Use sync logout to prevent navigation issues
          logoutSync();
          // Redirect to login
          if (typeof window !== 'undefined') {
            window.location.href = '/';
          }
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Export the configured axios instance
export default axiosInstance;

